import { FinGroups, getCategoryExperience } from '#dictionary'
import ProfileService from '#profile/ProfileService'

import aggregateData, { type AggregatedData } from './aggregateData'
import AssetDrawDownService from './AssetDrawDownService'
import { aggregateRecordsByYear } from './financialRecords'
import { FinancialRecord } from './financialRecords'
import {
  AssetGenerator,
  LiabilityGenerator,
  TaxesGenerator,
  TimelineGenerator,
  TransactionGenerator,
} from './generators'
import SavingsGenerator from './generators/SavingsGenerator'
import type { MemberRetirementAges } from './memberRetirementAges'
import type { ITaxer } from './Taxer'
import { generateDoubleEntryRecords } from './timeline'
import type { TimelineRecord } from './timeline/TimelineRecord'
import type {
  FinancialRecordSummary,
  Id,
  RecordsWithChildrenMap,
  TotalTaxesCalculationResult,
} from './types'

const MAX_DRAWDOWN_ITERATION_RUNS = 100

export type CalculationResult = {
  year: number
  timeline: readonly TimelineRecord[]
  aggregatedData: AggregatedData
  taxResult: TotalTaxesCalculationResult | null
}

class Calculator {
  private readonly records: readonly FinancialRecord[]
  private readonly recordsMap: RecordsWithChildrenMap
  private groupedRecordIdsByYear: Map<number, readonly string[]> = new Map()
  private generatorsMap: Map<Id, TimelineGenerator> = new Map()
  private taxesGenerator: TaxesGenerator
  private savingsGenerator: SavingsGenerator
  private assetDrawDownService: AssetDrawDownService

  // Held for future age-aware generators (ZL-738 follow-up). The field is
  // required at construction to enforce that callers compute the map at the
  // boundary instead of letting tax/calc internals read `member.retirementAge`.
  public readonly retirementAges: MemberRetirementAges

  constructor(
    allRecords: readonly FinancialRecord[],
    private readonly taxer: ITaxer,
    private readonly profile: ProfileService,
    // TODO: drop `enabledQuestIds` — callers now decide up-front which
    // quest records to include in `allRecords` (see
    // `useFinancialProjection`: baseline gets submissions only, scenario
    // gets `[...adjustedSubmissions, ...questRecords]`). The filter here
    // is dead weight; `undefined`/`[]` from every real caller means the
    // predicate below is a no-op for records without `questId` and a
    // silent drop for anything with one. If per-quest visibility ever
    // becomes a UI feature, we can reintroduce this via a caller-owned
    // filter step instead of a Calculator constructor arg.
    enabledQuestIds: string[] | undefined,
    private readonly applyDrawdowns: boolean,
    retirementAges: MemberRetirementAges
  ) {
    this.retirementAges = retirementAges
    this.records = allRecords.filter(record => {
      const qid = record.questId
      return qid == null || enabledQuestIds?.includes(qid)
    })

    this.recordsMap = this.makeRecordsMap(this.records)
    this.assetDrawDownService = new AssetDrawDownService(this.records)
    this.taxesGenerator = new TaxesGenerator(this.taxer, this.profile)
    this.savingsGenerator = new SavingsGenerator()
    this.initializeGenerators()
  }

  calculate(): CalculationResult[] {
    this.groupedRecordIdsByYear = aggregateRecordsByYear(
      this.records,
      this.profile.projectionEndYear
    )
    const calculationResults: CalculationResult[] = []

    for (const year of this.groupedRecordIdsByYear.keys()) {
      if (year > this.profile.projectionEndYear) {
        break
      }

      const calculationResult = this.applyDrawdowns
        ? this.calculateForYearWithDrawdowns(year)
        : this.calculateForYear(year)

      calculationResults.push(calculationResult)
    }

    return calculationResults
  }

  // TODO(ZL-961): this retry loop re-invokes every generator for the same
  // `year` on each recursion. That only works because every generator's
  // `_generate` is idempotent per year (see TimelineGenerator). If a future
  // generator adds mutable per-year state, retries will silently corrupt
  // downstream results (ZL-957 was exactly this class of bug in the
  // LiabilityGenerator). Long-term fix: pure-function generators with a
  // snapshotted `YearState` — no per-instance discipline needed.
  calculateForYearWithDrawdowns(
    year: number,
    drawDownRecipients?: FinancialRecordSummary[],
    run = 1 // #dk the Fuse
  ): CalculationResult {
    const calculationResult = this.calculateForYear(year, drawDownRecipients)

    const freeCash = calculationResult.aggregatedData.freeCash.totalValue

    const drawdowns = calculationResult.aggregatedData.drawDowns.totalValue

    if (freeCash >= 0) {
      return calculationResult
    }

    const taxesOverspent = Math.abs(freeCash)
    const amountToDrawdown = taxesOverspent + drawdowns

    const drawDownResult = this.assetDrawDownService.getRecipients(
      amountToDrawdown,
      calculationResult.aggregatedData.assetsBreakdown
    )

    if (
      drawDownResult.recipients.length > 0 &&
      run <= MAX_DRAWDOWN_ITERATION_RUNS
    ) {
      return this.calculateForYearWithDrawdowns(
        year,
        drawDownResult.recipients,
        run + 1
      )
    }

    return calculationResult
  }

  calculateForYear(
    year: number,
    drawDownRecipients?: FinancialRecordSummary[]
  ): CalculationResult {
    const records = this.findRecordsActiveInYear(year)

    const timeline: TimelineRecord[] = []

    for (const record of records) {
      const generator = this.generatorsMap.get(record.id)
      const result = generator
        ? generator.generate(year, drawDownRecipients)
        : { timeline: [], finalValue: 0 }

      for (const timelineRecord of result.timeline) {
        timeline.push(timelineRecord)
      }
    }

    const taxes = this.taxesGenerator.generate(year, timeline)
    timeline.push(...taxes.timelineRecords)

    const doubleEntryRecords = generateDoubleEntryRecords(timeline)
    timeline.push(...doubleEntryRecords)

    const savings = this.savingsGenerator.generate(year, timeline)
    timeline.push(...savings)

    const aggregatedData = aggregateData(timeline)

    return {
      year,
      timeline,
      aggregatedData,
      taxResult: taxes.taxCalculationResult,
    }
  }

  private initializeGenerators(): void {
    this.records.forEach(record => {
      if (!this.generatorsMap.has(record.id)) {
        const generator = this.createGenerator(record)
        if (generator) {
          this.generatorsMap.set(record.id, generator)
        }
      }
    })
  }

  // TODO: #could move to TimelineGenerator.forRecord(record)
  private createGenerator(
    record: FinancialRecord
  ): TimelineGenerator | undefined {
    const finGroup = getCategoryExperience(record.finCategory).finGroup
    const children = this.recordsMap.get(record.id)!.children
    const transfers = this.records.filter(
      it => it.t_fromAssetId === record.id || it.t_toAssetId === record.id
    )

    // TODO: #should move this to TimelineGenerator.forRecord(record)
    if (
      ([FinGroups.Income, FinGroups.Expenses] as string[]).includes(finGroup) &&
      !record.finCategory.isTechnical
    ) {
      return new TransactionGenerator(record)
    } else if (finGroup === FinGroups.Liabilities) {
      return new LiabilityGenerator(record, children)
    } else if (finGroup === FinGroups.Assets) {
      return new AssetGenerator(
        record,
        children,
        transfers,
        this.profile.projectionStartDate,
        this.profile.projectionEndYear
      )
    }

    return undefined
  }

  private findRecordsActiveInYear(year: number): readonly FinancialRecord[] {
    const recordIds = this.groupedRecordIdsByYear.get(year)
    if (!recordIds) return []

    const records: FinancialRecord[] = []
    for (const id of recordIds) {
      const recordData = this.recordsMap.get(id)
      if (recordData?.record) {
        records.push(recordData.record)
      }
    }
    return records
  }

  private makeRecordsMap(
    records: readonly FinancialRecord[]
  ): RecordsWithChildrenMap {
    const recordsMap = new Map(
      records.map(record => [
        record.id,
        { record, children: [] as FinancialRecord[] },
      ])
    )

    records.forEach(record => {
      if (record.parentId) {
        const parent = recordsMap.get(record.parentId)
        if (parent) {
          parent.children.push(record)
        }
      }
    })

    return recordsMap
  }
}

export default Calculator
